/**
 * BulkEnrichAutoModal — confirmation modal for bulk auto-enrichment.
 *
 * Groups contacts into three eligibility buckets (no phone-only groups):
 *   - Will enrich (eligible)
 *   - Skipped — enrichment in progress
 *   - Skipped — recently enriched (<1 year)
 *
 * On confirm, calls the bulk-action/enrich/ endpoint. Supports both
 * non-selectAll (entity_ids) and selectAll (filter_groups) modes.
 * Async dispatch is forwarded to the caller via onAsyncDispatched.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { formatCredits, getCostPerContact } from '@/lib/billing/creditCost';
import { useCreditBalance } from '@/lib/billing/useCreditBalance';
import type { BulkActionSelection } from '@/lib/crm/core/CRMObjectClient';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { translateBulkEnrichError } from '@/lib/crm/core/errors';
import type { CrmObject, FilterGroup } from '@/types/crm';

import { EnrichmentEligibilitySummary } from './EnrichmentEligibilitySummary';
import { useAutoEnrichmentEligibility } from './useAutoEnrichmentEligibility';
import { useEnrichmentAllowance } from './useEnrichmentAllowance';

const MAX_SYNC_ITERATIONS = 20;

export interface BulkEnrichAutoModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Organization ID */
  orgId: number;
  /** Contacts to evaluate for eligibility display */
  contacts: CrmObject[];
  /** Callback when modal closes */
  onClose: () => void;
  /** Called with jobId + totalCount when the backend dispatches an async job */
  onAsyncDispatched: (jobId: number, totalCount: number) => void;
  /** Called after a sync enrich completes successfully */
  onSyncComplete: () => void;
  /** When true, use filterGroups / excludedEntityIds instead of contacts entity_ids */
  isSelectAllMode?: boolean;
  /** Total matching records in select-all mode (used for CTA copy) */
  totalCount?: number;
  /** Filter groups to pass in selectAll mode */
  filterGroups?: FilterGroup[];
  /** Excluded entity IDs in selectAll mode */
  excludedEntityIds?: string[];
  /** Full-text search string (selectAll mode only) */
  search?: string;
  /** Search property names (selectAll mode only) */
  searchProperties?: string[];
}

export function BulkEnrichAutoModal({
  isOpen,
  orgId,
  contacts,
  onClose,
  onAsyncDispatched,
  onSyncComplete,
  isSelectAllMode,
  totalCount,
  filterGroups,
  excludedEntityIds,
  search,
  searchProperties,
}: BulkEnrichAutoModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    infoMap,
    isLoading: isLoadingEligibility,
    isError: isErrorEligibility,
  } = useAutoEnrichmentEligibility(
    isOpen ? orgId : undefined,
    isOpen ? contacts : []
  );

  const { data: creditBalance } = useCreditBalance(isOpen ? orgId : undefined);

  const { data: allowance, isError: isAllowanceError } = useEnrichmentAllowance(
    isOpen ? orgId : undefined
  );

  const { willEnrich, inProgress, recentlyEnriched } = useMemo(() => {
    const willEnrich: CrmObject[] = [];
    const inProgress: CrmObject[] = [];
    const recentlyEnriched: CrmObject[] = [];

    for (const contact of contacts) {
      const info = infoMap.get(contact.entity_id);
      const status = info?.status ?? 'eligible';

      if (status === 'in_progress') {
        inProgress.push(contact);
      } else if (status === 'recently_enriched') {
        recentlyEnriched.push(contact);
      } else {
        willEnrich.push(contact);
      }
    }

    return { willEnrich, inProgress, recentlyEnriched };
  }, [contacts, infoMap]);

  const creditCount = isSelectAllMode
    ? (totalCount ?? contacts.length)
    : willEnrich.length;

  const remaining = allowance?.remaining;
  const overCap =
    !isAllowanceError && remaining !== undefined && creditCount > remaining;

  const { standardCost, estimatedCost, available, willExceed } = useMemo(() => {
    const standardCost = getCostPerContact(creditBalance?.costs, 'standard');
    const estimatedCost = creditCount * standardCost;
    const available = creditBalance?.available;
    const willExceed = available !== undefined && estimatedCost > available;
    return { standardCost, estimatedCost, available, willExceed };
  }, [creditCount, creditBalance]);

  const willEnrichEntityIds = useMemo(
    () => willEnrich.map(o => o.entity_id),
    [willEnrich]
  );
  const inProgressEntityIds = useMemo(
    () => inProgress.map(o => o.entity_id),
    [inProgress]
  );
  const recentlyEnrichedEntityIds = useMemo(
    () => recentlyEnriched.map(o => o.entity_id),
    [recentlyEnriched]
  );

  const handleConfirm = useCallback(async () => {
    if (willEnrich.length === 0 && !isSelectAllMode) {
      return;
    }

    setIsSubmitting(true);
    try {
      const client = createCRMObjectClient(orgId, 'ahoy_contact');
      // Pick the discriminator that matches the user's intent:
      // - explicit row selection → entity_ids
      // - select-all + a positive selector → filters
      // - select-all without any selector → all (explicit opt-in to unscoped)
      let selection: BulkActionSelection;
      if (!isSelectAllMode) {
        selection = {
          kind: 'entity_ids',
          entityIds: contacts.map(c => c.entity_id),
        };
      } else if (
        (filterGroups && filterGroups.length > 0) ||
        (search != null && search !== '')
      ) {
        selection = {
          kind: 'filters',
          filterGroups,
          excludedEntityIds,
          search,
          searchProperties,
        };
      } else {
        selection = { kind: 'all', excludedEntityIds };
      }
      const firstResponse = await client.bulkEnrich('ahoy_contact', selection);

      if (firstResponse.mode === 'async') {
        onAsyncDispatched(firstResponse.job_id, firstResponse.total_count);
        onClose();
        return;
      }

      // Only the select-all / filter-based path can produce `has_more` — the
      // explicit entity_ids path always processes the full set in a single
      // chunk and returns has_more=false. Loop on the same selection: each
      // call advances because previously-enriched contacts become ineligible
      // (in_progress) on subsequent eligibility checks.
      let totalEnriched = firstResponse.enriched_count;
      let hasMore = firstResponse.has_more;
      let iterations = 1;
      let hitIterationCap = false;

      while (hasMore) {
        iterations += 1;
        if (iterations > MAX_SYNC_ITERATIONS) {
          hitIterationCap = true;
          break;
        }
        const response = await client.bulkEnrich('ahoy_contact', selection);

        if (response.mode === 'async') {
          onAsyncDispatched(response.job_id, response.total_count);
          onClose();
          return;
        }

        totalEnriched += response.enriched_count;
        hasMore = response.has_more;
      }

      if (hitIterationCap) {
        toast.error(
          'Enrichment incomplete. Some contacts may not have been queued. Try again.'
        );
      } else {
        toast.success('Enrichment started', {
          description: `${totalEnriched} contact${totalEnriched === 1 ? '' : 's'} queued for enrichment`,
        });
      }
      onSyncComplete();
      onClose();
    } catch (error) {
      const resp = (
        error as {
          response?: {
            error_code?: string;
            error_type?: string;
            cap?: number;
            remaining?: number;
          };
        }
      )?.response;
      if (resp?.error_code === 'enrichment_hourly_cap_exceeded') {
        const capDescription =
          resp.cap !== undefined && resp.remaining !== undefined
            ? `You've reached your hourly enrichment limit of ${resp.cap}. ${resp.remaining} more contact${resp.remaining === 1 ? '' : 's'} can be enriched this hour.`
            : "You've reached your hourly enrichment limit. Try again later.";
        toast.error('Hourly enrichment limit reached', {
          description: capDescription,
        });
      } else {
        toast.error('Enrichment failed', {
          description: translateBulkEnrichError(resp?.error_type),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    orgId,
    isSelectAllMode,
    willEnrich,
    contacts,
    filterGroups,
    excludedEntityIds,
    search,
    searchProperties,
    onAsyncDispatched,
    onClose,
    onSyncComplete,
  ]);

  const isLoading = isLoadingEligibility;
  const isError = isErrorEligibility;

  let confirmButtonLabel: string;
  if (isError) {
    confirmButtonLabel = 'Unable to enrich';
  } else if (!isSelectAllMode && willEnrich.length === 0) {
    confirmButtonLabel = 'No contacts to enrich';
  } else {
    // In select-all mode `contacts.length` is the *loaded* page slice — using
    // it would undercount the actual records the backend will process. Prefer
    // the page-level totalCount (matches filter_groups), falling back to
    // contacts.length only if the parent didn't pass one.
    const count = isSelectAllMode
      ? (totalCount ?? contacts.length)
      : willEnrich.length;
    confirmButtonLabel = `Enrich ${count} contact${count === 1 ? '' : 's'}`;
  }

  let allowanceContent: React.ReactNode = null;
  if (remaining !== undefined && overCap) {
    allowanceContent = (
      <p className='text-xs text-[var(--color-text-warning-primary)]'>
        {`This selection exceeds your hourly enrichment limit of ${allowance?.cap}. You can enrich ${remaining} more this hour – reduce your selection or try again later.`}
      </p>
    );
  } else if (remaining !== undefined) {
    allowanceContent = (
      <p className='text-xs text-fg-secondary'>
        {`You can enrich up to ${remaining} more contact${remaining === 1 ? '' : 's'} this hour.`}
      </p>
    );
  }

  const creditContent =
    creditBalance?.enforcement_enabled && creditCount > 0 ? (
      <>
        <p className='text-xs text-fg-secondary'>
          {`This will use ${formatCredits(estimatedCost)} (${standardCost} per contact).`}
          {available !== undefined &&
            ` You have ${formatCredits(available)} remaining.`}
        </p>
        {willExceed && (
          <p className='text-xs text-[var(--color-text-warning-primary)]'>
            This will exceed your credit balance.
          </p>
        )}
      </>
    ) : null;

  // Select-all: `contacts` is only the loaded page slice (often empty) — use
  // the page-level totalCount (matches filter_groups), falling back to
  // contacts.length only if the parent didn't pass one.
  const selectAllCount = totalCount ?? contacts.length;

  const footer = (
    <div className='flex items-center justify-end gap-3'>
      <Button
        color='secondary'
        size='md'
        onClick={onClose}
        isDisabled={isSubmitting}
      >
        Cancel
      </Button>
      <Button
        color='primary'
        size='md'
        onClick={handleConfirm}
        isDisabled={
          isSubmitting ||
          isLoading ||
          isError ||
          overCap ||
          (!isSelectAllMode && willEnrich.length === 0)
        }
        isLoading={isSubmitting}
      >
        {confirmButtonLabel}
      </Button>
    </div>
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose]
  );

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isSubmitting}
    >
      <Modal className='sm:max-w-lg'>
        <Dialog
          title='Enrich contacts'
          isCloseDisabled={isSubmitting}
          footer={footer}
        >
          <div className='space-y-3'>
            <EnrichmentEligibilitySummary
              orgId={orgId}
              isLoading={isLoading}
              isError={isError}
              isSelectAllMode={isSelectAllMode}
              totalContactsCount={contacts.length}
              selectAllCount={selectAllCount}
              willEnrichEntityIds={willEnrichEntityIds}
              inProgressEntityIds={inProgressEntityIds}
              recentlyEnrichedEntityIds={recentlyEnrichedEntityIds}
              allowanceContent={allowanceContent}
              creditContent={creditContent}
            />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
